"use client";

import Link from "next/link";
import { ArrowRight, Mail, Phone, Building2, Clock, Newspaper, Send } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { ctaSection, contactInfo } from "@/lib/data/homepage";

/**
 * CTA + Contact — Premium end-cap. Full-bleed gradient panel with
 * contact form on right, contact details on left, dramatic typography.
 */
export function CtaContact() {
  return (
    <section id="contact" className="relative section-pad">
      <Container>
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl glass">
            {/* Animated backdrop */}
            <div className="pointer-events-none absolute inset-0 -z-10">
              <div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-[var(--color-crimson)] opacity-25 blur-[140px] animate-pulse-glow" />
              <div
                className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-[var(--color-purple)] opacity-25 blur-[140px] animate-pulse-glow"
                style={{ animationDelay: "2s" }}
              />
              <div
                className="absolute top-1/2 left-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[140px] animate-pulse-glow"
                style={{ animationDelay: "4s" }}
              />
            </div>

            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-0">
              {/* LEFT — dramatic CTA copy */}
              <div className="p-10 sm:p-14 lg:p-16 flex flex-col justify-center">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-crimson)] w-fit">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-crimson)]" />
                  {ctaSection.eyebrow}
                </span>
                <h2 className="mt-6 text-balance display-cta">
                  {ctaSection.title}
                </h2>
                <p className="mt-5 text-body-sm leading-relaxed text-[rgb(var(--fg-muted))]">
                  {ctaSection.desc}
                </p>
                <p className="mt-4 text-body-sm leading-relaxed text-[rgb(var(--fg-muted))]">
                  {ctaSection.desc2}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    href="#contact-form"
                    className="btn-primary-gradient h-13 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-crimson)/40]"
                  >
                    Request a Demo <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    href="#contact-form"
                    className="btn-glass h-13 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                  >
                    Schedule a Demo
                  </Link>
                </div>
              </div>

              {/* RIGHT — contact details */}
              <div id="contact-form" className="p-10 sm:p-14 lg:p-16 border-t lg:border-t-0 lg:border-l border-[rgb(var(--border)/0.18)]">
                <div className="eyebrow-tiny text-[var(--color-cyan)] mb-4">
                  Contact
                </div>
                <h3 className="mb-6">Get in Touch</h3>

                <div className="space-y-4">
                  <ContactRow
                    icon={Mail}
                    label="General Inquiries"
                    value={contactInfo.general.email}
                    href={`mailto:${contactInfo.general.email}`}
                    color="var(--color-cyan)"
                  />
                  <ContactRow
                    icon={Send}
                    label="Sales & Demos"
                    value={contactInfo.sales.email}
                    sub="Response within 24 hours"
                    href={`mailto:${contactInfo.sales.email}`}
                    color="var(--color-crimson)"
                  />
                  <ContactRow
                    icon={Newspaper}
                    label="Media & Press"
                    value={contactInfo.press.email}
                    href={`mailto:${contactInfo.press.email}`}
                    color="var(--color-purple)"
                  />
                  <ContactRow
                    icon={Building2}
                    label="Headquarters"
                    value={contactInfo.hq}
                    color="var(--color-amber)"
                  />
                  <ContactRow
                    icon={Clock}
                    label="Office Hours"
                    value={contactInfo.hours}
                    color="var(--color-emerald)"
                  />
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function ContactRow({
  icon: Icon,
  label,
  value,
  sub,
  href,
  color,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string;
  sub?: string;
  href?: string;
  color: string;
}) {
  const content = (
    <div className="flex items-start gap-3 group">
      <span
        className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
        style={{
          background: `color-mix(in oklch, ${color} 18%, transparent)`,
          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${color} 35%, transparent)`,
        }}
      >
        <Icon className="h-4 w-4" style={{ color }} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="eyebrow-tiny font-semibold text-[rgb(var(--fg-subtle))]">
          {label}
        </div>
        <div className="text-sm font-semibold mt-0.5 group-hover:text-[var(--color-crimson)] transition-colors">
          {value}
        </div>
        {sub && <div className="text-[0.7rem] text-[rgb(var(--fg-subtle))] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block">
      {content}
    </a>
  ) : (
    content
  );
}
